<template>
  <div class="news-page">
    <!-- هدر صفحه -->
    <div class="page-header">
      <div class="page-header-inner">
        <h1 class="page-title">
          <i class="fas fa-newspaper"></i>
          اخبار ورزشی
        </h1>

        <!-- جستجو -->
        <div class="search-box">
          <input
              v-model="searchInput"
              @keyup.enter="doSearch"
              type="text"
              placeholder="جستجو در اخبار..."
              class="search-input"
          />
          <button @click="doSearch" class="search-btn">
            <i class="fas fa-search"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="page-content">

      <!-- فیلتر منابع -->
      <div class="source-tabs">
        <button
            :class="['source-tab', activeSource === null ? 'active' : '']"
            @click="setSource(null)"
        >
          همه منابع
        </button>
        <button
            v-for="src in sources"
            :key="src.name"
            :class="['source-tab', `tab-${src.name}`, activeSource === src.name ? 'active' : '']"
            @click="setSource(src.name)"
        >
          <span class="source-dot" :class="`dot-${src.name}`"></span>
          {{ src.label }}
        </button>
      </div>

      <!-- loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>در حال دریافت اخبار...</p>
      </div>

      <!-- خطا -->
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button @click="loadNews" class="retry-btn">تلاش مجدد</button>
      </div>

      <!-- خالی -->
      <div v-else-if="news.length === 0" class="empty-state">
        <i class="fas fa-search"></i>
        <p>خبری پیدا نشد</p>
        <p v-if="activeSearch" class="empty-hint">جستجوی «{{ activeSearch }}» نتیجه‌ای نداشت</p>
      </div>

      <!-- گرید اخبار -->
      <div v-else class="news-grid">
        <article
            v-for="item in news"
            :key="item.id"
            class="news-card"
            @click="openNews(item)"
        >
          <!-- تصویر -->
          <div class="card-image">
            <img
                v-if="item.imageUrl"
                :src="proxyImage(item.imageUrl)"
                :alt="item.title"
                @error="onImgError($event)"
                loading="lazy"
            />
            <div v-else class="no-image">
              <i class="fas fa-newspaper"></i>
            </div>
            <span :class="['source-badge', `badge-${item.source.name}`]">
              {{ sourceLabel(item.source) }}
            </span>
          </div>

          <!-- محتوا -->
          <div class="card-body">
            <h3 class="card-title">{{ item.title }}</h3>
            <p class="card-summary">{{ item.summary }}</p>

            <div class="card-meta">
              <span class="meta-time">
                <i class="far fa-clock"></i>
                {{ timeAgo(item.publishDate) }}
              </span>
              <a
                  :href="item.sourceUrl"
                  target="_blank"
                  rel="noopener"
                  class="meta-link"
                  @click.stop
              >
                منبع <i class="fas fa-external-link-alt"></i>
              </a>
            </div>
          </div>
        </article>
      </div>

      <!-- صفحه‌بندی -->
      <div v-if="pagination && pagination.totalPages > 1" class="pagination">
        <button
            :disabled="!pagination.hasPrev"
            @click="changePage(pagination.page - 1)"
            class="page-btn"
        >
          <i class="fas fa-chevron-right"></i>
          قبلی
        </button>

        <span class="page-info">
          صفحه {{ pagination.page }} از {{ pagination.totalPages }}
        </span>

        <button
            :disabled="!pagination.hasNext"
            @click="changePage(pagination.page + 1)"
            class="page-btn"
        >
          بعدی
          <i class="fas fa-chevron-left"></i>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import apiService from '@/services/api'

export default {
  name: 'NewsPage',
  data() {
    return {
      news: [],
      pagination: null,
      sources: [],
      loading: false,
      error: null,
      activeSource: null,
      searchInput: '',
      activeSearch: '',
      currentPage: 1,
    }
  },

  methods: {
    slugify(title) {
      if (!title) return ''
      return title
          .trim()
          .replace(/\u200c|\u200b|\u200d|\ufeff/g, '-')
          .replace(/[,;:.!?"'()]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '')
    },
    async loadNews() {
      this.loading = true
      this.error = null
      try {
        const result = await apiService.getNews({
          page: this.currentPage,
          limit: 18,
          source: this.activeSource,
          search: this.activeSearch || null,
        })
        if (result && result.news) {
          this.news = result.news
          this.pagination = result.pagination
        } else {
          this.news = []
        }
      } catch (e) {
        this.error = 'خطا در دریافت اخبار. لطفاً دوباره تلاش کنید.'
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    async loadSources() {
      try {
        const srcs = await apiService.getNewsSources();
        // اگر srcs آرایه است
        if (Array.isArray(srcs)) {
          this.sources = srcs;
        }
        // اگر srcs آبجکت است و خاصیت sources دارد
        else if (srcs && srcs.sources) {
          this.sources = srcs.sources;
        }
      } catch (e) {
        this.sources = [
          { name: 'varzesh3', label: 'ورزش۳' },
          { name: 'khabarvarzeshi', label: 'خبر ورزشی' },
          { name: 'isna', label: 'ایسنا' },
          { name: 'tasnim', label: 'تسنیم' },
          { name: 'yjc', label: 'باشگاه خبرنگاران جوان' },
        ];
      }
    },

    setSource(src) {
      this.activeSource = src
      this.currentPage = 1
      this.loadNews()
    },

    doSearch() {
      this.activeSearch = this.searchInput.trim()
      this.currentPage = 1
      this.loadNews()
    },

    changePage(page) {
      this.currentPage = page
      this.loadNews()
      window.scrollTo({top: 0, behavior: 'smooth'})
    },

    openNews(item) {
      this.$router.push({name: 'NewsDetail', params: {slug: item.id + '-' + this.slugify(item.title)}})
    },

    onImgError(e) {
      e.target.style.display = 'none'
      e.target.parentElement.classList.add('no-image-fallback')
    },

    sourceLabel(source) {
      // اگر source یک آبجکت است (ساختار جدید API)
      if (source && typeof source === 'object') {
        const map = {varzesh3: 'ورزش۳', khabarvarzeshi: 'خبر ورزشی', isna: 'ایسنا', tasnim: 'تسنیم', yjc: 'باشگاه خبرنگاران جوان', tabnak: 'تابناک'}
        return map[source.name] || source.name || 'خبرگزاری'
      }
      // اگر source یک رشته است (ساختار قدیمی یا fallback)
      const map = {varzesh3: 'ورزش۳', khabarvarzeshi: 'خبر ورزشی', isna: 'ایسنا', tasnim: 'تسنیم', yjc: 'باشگاه خبرنگاران جوان', tabnak: 'تابناک'}
      return map[source] || source || 'خبرگزاری'
    },

    proxyImage(url) {
      return url || null
    },

    timeAgo(dateStr) {
      if (!dateStr) return ''
      const now = new Date()
      const date = new Date(dateStr)
      const diff = Math.floor((now - date) / 1000) // seconds
      if (diff < 60) return 'همین الان'
      if (diff < 3600) return `${Math.floor(diff / 60)} دقیقه پیش`
      if (diff < 86400) return `${Math.floor(diff / 3600)} ساعت پیش`
      if (diff < 604800) return `${Math.floor(diff / 86400)} روز پیش`
      return date.toLocaleDateString('fa-IR')
    },
  },

  async mounted() {
    // اگه query string داشت اعمال کن
    if (this.$route.query.source) this.activeSource = this.$route.query.source
    if (this.$route.query.search) {
      this.searchInput = this.$route.query.search
      this.activeSearch = this.$route.query.search
    }
    await Promise.all([this.loadSources(), this.loadNews()])
  },

  watch: {
    '$route.query'(q) {
      this.activeSource = q.source || null
      this.searchInput = q.search || ''
      this.activeSearch = q.search || ''
      this.currentPage = 1
      this.loadNews()
    }
  },
}
</script>

<style scoped>
.news-page {
  min-height: 100vh;
  width: 100%;
  direction: rtl;
  font-family: 'Vazirmatn', sans-serif;
}

/* ===== هدر صفحه ===== */
.page-header {
  background: 
    url('~@/assets/images/header-ball-bg.svg') no-repeat left 4% center / 280px auto,
    linear-gradient(135deg, #022c22 0%, #064e3b 45%, #047857 85%, #059669 100%);
  padding: 28px 0;
  position: relative;
  overflow: hidden;

  @include mobile {
    background-position: left -30px center;
    background-size: 180px auto;
  }
}

.page-header-inner {
  margin: 0 auto;
  padding: 0 20px;
  max-width: 1400px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.page-title {
  color: #fff;
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.page-title i {
  color: #93c5fd;
}

/* جستجو */
.search-box {
  display: flex;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 30px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.search-input {
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  padding: 10px 18px;
  font-size: 0.9rem;
  width: 220px;
  font-family: inherit;
  direction: rtl;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.search-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.search-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

/* ===== محتوا ===== */
.page-content {
  margin: 0 auto;
  padding: 24px 20px;
  max-width: 1400px;
}

/* ===== تب‌های منبع ===== */
.source-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.source-tab {
  padding: 8px 18px;
  border-radius: 20px;
  border: 2px solid #e2e8f0;
  background: #fff;
  color: #475569;
  cursor: pointer;
  font-size: 0.88rem;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.source-tab:hover {
  border-color: #94a3b8;
}

.source-tab.active {
  background: #1e3a8a;
  color: #fff;
  border-color: #1e3a8a;
}

.source-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

/* رنگ dot-{source} از style.scss سراسری می‌آید */

/* ===== گرید اخبار ===== */
.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.news-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
}

.news-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

/* تصویر کارت — نسبت ۱۶:۹ وابسته به عرض کارت
   به‌جای ارتفاع ثابت؛ عکس‌های خبری به‌جای بریده‌شدن کامل نمایش داده می‌شوند */
.card-image {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #e2e8f0;
  overflow: hidden;
}

/* مرورگرهای خیلی قدیمی بدون پشتیبانی aspect-ratio */
@supports not (aspect-ratio: 16 / 9) {
  .card-image {
    height: 210px;
  }
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s;
}

.news-card:hover .card-image img {
  transform: scale(1.04);
}

.no-image, .no-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #94a3b8;
  font-size: 2.5rem;
  background: linear-gradient(135deg, #e2e8f0, #f1f5f9);
}

/* badge منبع */
.source-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #fff;
}

/* رنگ badge-{source} از style.scss سراسری می‌آید */

/* بدنه کارت */
.card-body {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.5;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-summary {
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.6;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0 0 12px 0;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: #94a3b8;
  border-top: 1px solid #f1f5f9;
  padding-top: 10px;
  margin-top: auto;
}

.meta-time {
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-link {
  color: #3b82f6;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-link:hover {
  color: #1d4ed8;
}

/* ===== حالت‌های خاص ===== */
.loading-state, .error-state, .empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #94a3b8;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #1e3a8a;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p, .error-state p, .empty-state p {
  font-size: 1rem;
  margin: 8px 0;
}

.error-state i, .empty-state i {
  font-size: 3rem;
  margin-bottom: 12px;
  display: block;
}

.empty-hint {
  font-size: 0.85rem;
}

.retry-btn {
  margin-top: 14px;
  padding: 8px 22px;
  background: #1e3a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
}

/* ===== صفحه‌بندی ===== */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 36px;
}

.page-btn {
  padding: 8px 20px;
  background: #fff;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  color: #1e3a8a;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #1e3a8a;
  color: #fff;
  border-color: #1e3a8a;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  color: #475569;
  font-size: 0.9rem;
}

/* ===== ریسپانسیو ===== */
@media (max-width: 900px) {
  .news-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .page-header-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    width: 100%;
  }

  .news-grid {
    grid-template-columns: 1fr;
  }

  .source-tabs {
    gap: 6px;
  }

  .source-tab {
    padding: 6px 12px;
    font-size: 0.82rem;
  }
}
</style>